import { Action, createActionGroup, createFeature, createFeatureSelector, createReducer, emptyProps, on } from '@ngrx/store';
import { Account, AccountClipboardPage, AccountPublicInfo, AccountUserInfo, cleanClipboard, combineAccountAndClipboardPages, getPublicAccountFromAccount } from './../../../globals/store/accounts.data';
import { ErrorStreamChange } from '../models/$error.model';
import { userActions } from './user.era';
import { createEntityAdapter, Dictionary, EntityState } from '@ngrx/entity';
import { jsonCopyObject } from 'globals/helpers/helpers';
// Entity adapter
export const adapter = createEntityAdapter<Account>();
export interface AccountsState extends EntityState<Account> {
  loading: boolean | string;
  loadingDocket: boolean
  unloading: boolean;
  loading_event_account: boolean;
  loaded_event_account:boolean;
  accountId: string
  trial_days_remaining:number
  suspended: { [accountId: string]: boolean }
  expired_messages: { [accountId: string]: boolean }
  selected_accountId: string;
  account: Partial<Account> | undefined;
  accountClipboardPages: Dictionary<AccountClipboardPage>
  clipboardPagesByAccount: Dictionary<Dictionary<AccountClipboardPage>> 
  public_info:{ [id: string]: AccountPublicInfo }
  supplementals: {[id:string]: Partial<Account> } // FIXME I dont know what these are for, but maybe they are used in untransfered parts of the project
  justInvitedIds: string[] // for use only for display purposes on the event list / home page
  selectedBudgetAccount: string
}
export const getAccountsState = createFeatureSelector<AccountsState>('accounts');

export const {
    selectIds,
    selectEntities,
    selectAll,
    selectTotal
  } = adapter.getSelectors(getAccountsState);

// Default data / initial state
const defaultAccountsState: AccountsState = {
    ids: [],
    entities: {},
    justInvitedIds: [], // needed 
    supplementals: {},
    loading:true,
    loadingDocket: true,
    unloading:false,
    loading_event_account:false,
    loaded_event_account:false,
    accountId: null,
    account: undefined,
    trial_days_remaining:null,
    expired_messages: {},
    public_info: {},
    selectedBudgetAccount: null,
    accountClipboardPages: {},
    clipboardPagesByAccount: {},
    selected_accountId: undefined,
    suspended: {}
}
export const initialStateCreator = (): AccountsState => defaultAccountsState// adapter.getInitialState(defaultAccountsState);


// Actions
export const accountActions = createActionGroup({
  source: 'Accounts',
  events: {
    'Select Account Id': (accountId: string) => ({ accountId }),
    'Update Selected Event Account': (account: Partial<Account>) => ({ account }),
    'Update Account Docket': (id: string, changes: Partial<Account>) => ({ id, changes }),
    'Update User Selected Account': (payload: string) => ({ payload }),
    'Update Account Public Info': (payload: { [id: string]: AccountPublicInfo }) => ({ payload }),
    'Remove Account Public Info': (id: string) => ({ id }),
    'Delete Account': (id: string) => ({ id }),
    'Remove Account User': (accountId: string, email: string, uid?:string, log_data?: Partial<ErrorStreamChange>) => ({ accountId, email, uid, log_data }),
    'Load Event Account': (account?:Partial<Account>) => ({ account }),
    'Update Event Account': (changes:Partial<Account>) => ({ changes }),
    'Add Many Accounts': (payload: Account[], uid:string) => ({ payload, uid }),
    'Accounts Loading': (payload:boolean) => ({ payload }),
    'Accounts Unloading': emptyProps(),
    'Modify Account': (id: string, changes: Partial<Account>) => ({ id, changes }),
    'Supplemental Account': (id: string, supplements: Partial<Account>) => ({ id, supplements }),
    'Modified Account User': (id: string, email:string, changes: Partial<AccountUserInfo>, log_data: Partial<ErrorStreamChange>) => ({ id, email, changes, log_data }),
    'Remove Account': (id: string) => ({ id }),
    'Account Success': emptyProps(),
    'Set Docket Loading Status': (payload: boolean) => ({ payload }),
    'Add To Just Invited List': (accountId: string) => ({ accountId }),
    'Reset Just Invited List': emptyProps(),
    'Select Budget Account': (payload:string) => ({ payload }),
    'Unload Event': emptyProps(), // this should be in the event.store
    'Add Clipboard Pages': (clipboardPages: AccountClipboardPage[]) => ({ clipboardPages }),
    'Add Many Accounts Clipboard Pages': (accountId: string, clipboardPages: AccountClipboardPage[]) => ({ accountId, clipboardPages }),
  }
})


export const accountsReducer = createReducer<AccountsState, Action<string>>(
  {...initialStateCreator()},
  on(accountActions.unloadEvent, (state) => ({ ...state, account: null, accountId: null, accountClipboardPages: {} })),
  on(accountActions.selectBudgetAccount, (state, { payload }) => ({ ...state, selectedBudgetAccount: payload })),
  on(accountActions.resetJustInvitedList, (state) => ({ ...state, justInvitedIds: [] })),
  on(accountActions.addToJustInvitedList, (state, { accountId }) => ({ ...state, justInvitedIds: [...state.justInvitedIds, accountId ] })),
  on(accountActions.setDocketLoadingStatus, (state, { payload }) => ({ ...state, loadingDocket: payload })),
  on(accountActions.updateUserSelectedAccount, (state, { payload }) => ({ ...state, selected_accountId: payload })),
  on(accountActions.addClipboardPages, (state, { clipboardPages }) => {
    const accountClipboardPages = jsonCopyObject(state.accountClipboardPages) || {}
    clipboardPages.filter(p => !p.hasOwnProperty('eventId')).forEach(page => accountClipboardPages[page.id] = page) // the filter for eventId is to fix a bug where new clipboard pages were created instead of new eventPages on the functions side of things.  
    return { ...state, accountClipboardPages, account: combineAccountAndClipboardPages(cleanClipboard(state.account), Object.values(accountClipboardPages || {})) }
  }),
  on(accountActions.accountsLoading, (state, { payload }) => ({ ...state, loading:payload, unloading:false })),
  on(accountActions.addManyAccountsClipboardPages, (state, { accountId, clipboardPages }) => {
    let account = state.entities[accountId]
    if (clipboardPages.length > 1) console.log({ account, clipboardPages })
    const clipboardPagesByAccount:any  = {} //{ [key: string]: AccountClipboardPage[] }
    clipboardPages.filter(p => !p.hasOwnProperty('eventId')) // same bug as above, the filter for eventId is to fix a bug where new clipboard pages were created instead of new eventPages on the functions side of things.
    .forEach(p => {
      if(!clipboardPagesByAccount[p.accountId]) clipboardPagesByAccount[p.accountId] = []
      clipboardPagesByAccount[p.accountId].push(p)
    }) 
    try{
      account = cleanClipboard(account) as Account
    }catch(e){
      console.error(e)
    }
    const combinedAccount = combineAccountAndClipboardPages(account, Object.values(clipboardPagesByAccount[accountId] || {})) as Account
    return { ...state, entities: { ...state.entities, [accountId]: { ...combinedAccount } }, clipboardPagesByAccount: {...state.clipboardPagesByAccount, [accountId]: clipboardPagesByAccount }}
  }),
  on(accountActions.updateSelectedEventAccount, (state, { account }) => {
    const updated_public_info = jsonCopyObject(state.public_info)
    if(updated_public_info[account.id] ){
      if(account.title) updated_public_info[account.id]['title'] = account.title
      if(account.photoURL) updated_public_info[account.id]['photoUrl'] = account.photoURL
    }
    return {...state, account: combineAccountAndClipboardPages({...state.account, ...account}, Object.values(state.accountClipboardPages)), public_info:updated_public_info}
  }),
  on(accountActions.updateAccountPublicInfo, (state, { payload: payloadRaw }) => {
      const public_suspended = {}
      const publicAccounts = {}
      const payload = jsonCopyObject(payloadRaw)
      Object.entries(payload).forEach(([ id, publicAccountObj ]) => {
        const publicAccount = {...publicAccountObj, docket: {...publicAccountObj.docket}} as AccountPublicInfo
        const account = state.entities[id]
        console.log({ id, publicAccount, account })
        if (!publicAccount.docket.status && !!account?.docket?.status) {
          publicAccount.docket.status = account.docket.status
        }
        public_suspended[id] = false
        if (publicAccount.docket.status === 'suspended') {
          public_suspended[id] = true
          payload[id].is_suspended = true
        }
        publicAccount.id = id
        publicAccounts[id] = getPublicAccountFromAccount(publicAccount, publicAccount.docket)
      })
      let rtn = {...state,
        public_info:{ ...state.public_info, ...publicAccounts },
        suspended: {...state.suspended, ...public_suspended }
      }
      return rtn
  }),
  on(accountActions.removeAccountPublicInfo, (state, { id }) => {
      const trimmed_public_info = {...state.public_info};
      delete trimmed_public_info[id];
      return {
        ...state,
        public_info: trimmed_public_info
      }
  }),
  on(accountActions.modifyAccount, (state, { id, changes }) => {
      return adapter.updateOne({
          id,
          changes,
      }, {...state, loading:id});
  }),
  on(accountActions.deleteAccount, (state, { id }) => {
    delete state.public_info[id]
    return adapter.removeOne(id, {...state, loading:true })
  }),
  on(accountActions.removeAccountUser, (state, { accountId, uid, email }) => {
      const acc = {...state.entities[accountId]};
      let update_obj;
      if(!uid || acc.users.includes(uid)){
        update_obj = {
          users: acc.users.filter(u=>u!==uid),
          userInfo: acc.userInfo.filter(u=>u.uid!==uid)
        } as Partial<Account>
        if(acc.admin.includes(uid)) update_obj.admin = acc.admin.filter(u=>u!==uid)
        if(acc.executive.includes(uid)) update_obj.executive = acc.executive.filter(u=>u!==uid)
        if(acc.leader.includes(uid)) update_obj.leader = acc.leader.filter(u=>u!==uid)
        if(acc.solo.includes(uid)) update_obj.solo = acc.solo.filter(u=>u!==uid)
      }else{
        update_obj = {
          userInfo: acc.userInfo.filter(u=>u.email!==email)
        }
      }
      return adapter.updateOne({
          id: accountId,
          changes: update_obj,
      }, {...state, loading:accountId});
  }),
  on(accountActions.loadEventAccount, (state, { account }) => {
      // console.log('state.account ', state.account);
      // console.log('action.account ', action.account);
      // console.log('action.account.id ', action.account.id);
      // if(state.account) console.log('state.account.id ', state.account.id);
      // console.log('action.account.users ', action.account.users);
      if(state.account && account && account.id === state.account.id && !account.users) return state
      const singleAccount = combineAccountAndClipboardPages(account, Object.values(state.accountClipboardPages))
      return adapter.upsertOne(singleAccount as Account, {...state, account: singleAccount})
  }),
  on(accountActions.updateEventAccount, (state, { changes }) => {
      return {...state, account: combineAccountAndClipboardPages({...state.account, ...changes}, Object.values(state.accountClipboardPages))}
  }),
  on(accountActions.accountsUnloading, (state) => ({ ...state, ids:[], entities:{}, loading:false, loaded:false, unloading:true, clipboardPagesByAccount: {} })),
  on(accountActions.addManyAccounts, (state, { payload, uid }) => {
      const supplementals = {...state.supplementals};
      payload.map(new_account=>{
        if(state.supplementals[new_account.id]){
          payload[new_account.id] = {...new_account, ...state.supplementals[new_account.id]}
          delete supplementals[new_account.id];
        }
      })
      payload.forEach(account => combineAccountAndClipboardPages(account, Object.values(state.clipboardPagesByAccount[account.id] || {})))
      return {...state, supplementals, ...adapter.upsertMany(payload, state)}
  }),
  on(accountActions.supplementalAccount, (state, { id, supplements }) => {
      if(state.entities[id]){
        const to_supplement = {...state.entities[id], ...supplements} 
        return {...state, entities:{...state.entities, [id]:to_supplement}}
      }
      return {...state, supplementals:{...state.supplementals, [id]:{...state.supplementals[id], ...supplements}}} 
  }),
  on(accountActions.modifiedAccountUser, (state, { id, email, changes, log_data }) => { 
      const acc = {...state.entities[id]};
      const userInfo = acc.userInfo.find(u=>u.email===email);
      if(!userInfo) return state;
      const updatedUserInfo = {...userInfo, ...changes}; 
      return adapter.updateOne({
          id: id,
          changes: {
            userInfo: acc.userInfo.map(u=>u.email===email ? updatedUserInfo : u)  
          },
      }, {...state, loading:id, unloading:false});
  }),
  on(accountActions.accountSuccess, (state) => ({ ...state, loading:false, unloading:false })), 
  on(accountActions.updateAccountDocket, (state, { id, changes }) => {
      changes = {...changes };
      const docket = {...changes.docket}
      const expired_messages = {}
      const suspended = {[id]:false}
      // may need a "was suspended" var to olny catch the first
      if(docket.status === 'suspended'){
        suspended[id] = true;
        changes.is_suspended = true;
        if(docket.stripeId){
          expired_messages['paid_'+id] = true
        }else{
          expired_messages['trial_'+id] = true
        }
      } else if (docket.status === 'active') { // since I cannot set accounts to suspended (and then back again) reliably, just trying to make sure that anything that shows that it is suspended is corrected
        suspended[id] = false
        changes.is_suspended = false
        delete expired_messages['paid_'+id]
        delete expired_messages['trial_'+id]
      }
      const account = (state.accountId === id) ? {...state.account, ...changes}: state.account;
      const public_info = JSON.parse(JSON.stringify(state.public_info)) // deep clone to avoid mutating state, since I need to update the public info's docket status if it exists.;
      if(public_info[id] && public_info[id].docket && changes.docket){
        public_info[id].docket.status = changes.docket.status;
      }
      return adapter.updateOne({
          id: id, 
          changes: changes,
      }, {
        ...state,
        account,
        public_info,
        suspended:{ ...state.suspended, ...suspended },
        expired_messages:{ ...state.expired_messages, ...expired_messages },
        loading:false
      });
  }),
  on(accountActions.selectAccountId, (state, { accountId }) => ({ ...state, accountId })),

  on(userActions.logout, (state) => ({ ...initialStateCreator() }))
);

export const accountsFeature = createFeature({
  name: 'accounts',
  reducer: accountsReducer
});

export const accountStateSelector = createFeatureSelector<AccountsState>(accountsFeature.name)

